import { ClubReference } from "@/types/club.types";
import { AvatarFields, CountryCode } from "@/types/common.types";
import { SportRef } from "@/types/sport.types";

/**
 * League management-team member roles for the league edit page's "Team" tab
 * (`GET /dashboard/v1/leagues/:id/members`). A member holds one or more of these
 * roles for THIS league only.
 *
 * Named `LeagueMemberRoleName` to avoid clashing with the legacy GraphQL
 * `LeagueMemberRole` type in `league.types.ts` (which is a `{ id, role }` object,
 * not a role-name union).
 */
export const LeagueMemberRoleNameValues = ["organizer", "manager", "referee"] as const;
export type LeagueMemberRoleName = (typeof LeagueMemberRoleNameValues)[number];

/**
 * REST-backed row for the league management-team list. Flat scalars plus the
 * member's league roles. Named `LeagueManagementMember` because the roster
 * concept already owns `LeagueTeamMember` / the GraphQL `LeagueMember`.
 */
export type LeagueManagementMember = AvatarFields & {
  /** Member user public id (`usr_…`). Identifies the membership within this league. */
  userId: string;
  name: string;
  surname: string;
  email: string;
  roles: LeagueMemberRoleName[];
  /**
   * Number of tournaments in this league the member is involved in (as organizer
   * or referee), de-duplicated per tournament. Scoped to this single league.
   */
  tournamentsCount: number;
  /**
   * Whether this membership represents an organization (vs. an individual).
   * `null` when never set. Drives the "Represent organization" switch prefill on
   * the edit-member modal (only meaningful when the member holds the `organizer`
   * role).
   */
  isOrganization: boolean | null;
  /** Organization display-name override for this membership, or `null`. */
  organizationName: string | null;
  /** Organization contact-email override for this membership, or `null`. */
  organizationEmail: string | null;
  /** Organization contact-phone override for this membership, or `null`. */
  organizationPhone: string | null;
  /**
   * Organization logo override: a resolved storage-file id + ready-to-render URL,
   * or `null` when no override is set. Used to prefill the logo upload field.
   */
  organizationLogo: { fileId: string; url: string | null } | null;
  /** Public contact-email override for this membership, or `null`. */
  contactEmail: string | null;
  /** Public contact-phone (E.164) override for this membership, or `null`. */
  contactPhone: string | null;
  /**
   * The contact email actually shown for this member, resolved server-side:
   * membership override → organization-representation channel → the person's own
   * `user.contactEmail` → `null`. Never the login email, so it is the only email
   * this page may surface.
   */
  effectiveContactEmail: string | null;
  /**
   * The contact phone actually shown for this member (E.164), resolved
   * server-side along the same chain as {@link LeagueManagementMember.effectiveContactEmail}.
   * Never the login phone.
   */
  effectiveContactPhone: string | null;
  /**
   * Whether this membership is surfaced as a public management-team member.
   * `null` when never set.
   */
  displayMember: boolean | null;
};

export const LeagueManagementMemberTableColumnValues = [
  "userId",
  "user",
  "contact",
  "roles",
  "tournaments",
  "actions",
] as const;
export type LeagueManagementMemberTableColumn =
  (typeof LeagueManagementMemberTableColumnValues)[number];

/**
 * REST-backed row for the league-teams list (`GET /dashboard/v1/leagues/:id/teams`).
 * Flat scalars plus single reference relations — `tournamentsCount` is the count
 * of confirmed tournaments the team has entered in this specific league.
 *
 * Distinct from {@link LeagueManagementMember} (the league-staff "Team" tab):
 * this is the read-only teams *list* page.
 */
export type LeagueTeamListItem = {
  /** Team public id (`tm_…`). */
  id: string;
  /**
   * Temporary numeric DB id, exposed only to bridge the public-facing team URL
   * (`{clientUrl}/team/{internalId}`) that still takes the numeric id. Deprecated
   * on the wire; remove once the client accepts publicIds.
   */
  internalId: number;
  title: string;
  image: string | null;
  country: CountryCode | null;
  email: string | null;
  phone: string | null;
  sport: SportRef | null;
  club: ClubReference | null;
  membersCount: number;
  tournamentsCount: number;
};

// The teams-list page columns (distinct from the league-staff management-member
// columns in `LeagueManagementMemberTableColumn`).
export const LeagueTeamsTableColumnValues = [
  "id",
  "title",
  "club",
  "sport",
  "contact",
  "members",
  "tournaments",
] as const;

export type LeagueTeamsTableColumn = (typeof LeagueTeamsTableColumnValues)[number];

export const LeagueTeamExportColumnValues = [
  "title",
  "country",
  "email",
  "phone",
  "sport",
  "clubName",
  "membersCount",
  "tournamentsCount",
] as const;

export type LeagueTeamExportColumn = (typeof LeagueTeamExportColumnValues)[number];
